import { useSearchParams } from 'react-router-dom'
import { AddCatalogEntry } from '@/components/catalog/AddCatalogEntry'
import { PageHeader } from '@/components/editorial/PageHeader'
import { useBrandAssetTotals, useExploreCatalog } from '@/hooks/useExplore'
import { BrandResults } from './BrandResults'
import {
	countryOptions,
	matchBrands,
	regionOfCountry,
	regionOptions,
	scopedBrands,
	scopedCountries,
} from './brand-catalog'
import { GeoFilterBar } from './GeoFilterBar'

/**
 * Explore - the brand browser.
 *
 * Brands are the destination and geography is a filter, so the page always shows a brand
 * grid and region/market only narrow which markets those brands are counted over. Opening
 * a brand is one click from landing, at any depth.
 *
 * Filter state lives in the URL (`?region=&country=&q=`), which makes a narrowed view
 * shareable and the browser's back button step back through the narrowing. Geography
 * changes push history; typing in the search box replaces it, so one search does not
 * bury the previous page under a keystroke per character.
 */
export function ExplorePage() {
	const [params, setParams] = useSearchParams()
	const { regions, countriesByRegion, isLoading, isError } = useExploreCatalog()

	const query = params.get('q') ?? ''
	// A `?country=` without its region (a shared or hand-edited link) still resolves.
	const rawRegion = params.get('region')
	const rawCountry = params.get('country')
	const regionCode = rawRegion ?? (rawCountry ? regionOfCountry(countriesByRegion, rawCountry) : null)
	// Honour the country only where it actually belongs to the active region.
	const countryCode =
		rawCountry && (countriesByRegion[regionCode ?? ''] ?? []).some((c) => c.code === rawCountry) ? rawCountry : null

	const patch = (next: Record<string, string | null>, replace = false) => {
		const sp = new URLSearchParams(params)
		for (const [key, value] of Object.entries(next)) {
			if (value) sp.set(key, value)
			else sp.delete(key)
		}
		setParams(sp, { replace })
	}

	// Narrowing runs on every render rather than behind a memo: the catalogue is tens of
	// brands over tens of markets, and the map it reads is rebuilt per render anyway, so a
	// memo here would never hit its cache.
	// Scope-wide totals, read once for the region in view (or the world). The funnel's own payloads
	// count per region, so they cannot answer this across regions — see `useBrandAssetTotals`.
	const { data: brandTotals = {} } = useBrandAssetTotals(regionCode)
	const inScope = scopedBrands(scopedCountries(regions, countriesByRegion, regionCode, countryCode), brandTotals)
	const brands = matchBrands(inScope, query)

	const region = regions.find((r) => r.code === regionCode) ?? null
	const country = (countriesByRegion[regionCode ?? ''] ?? []).find((c) => c.code === countryCode) ?? null
	const scopeLabel = country?.label ?? region?.label ?? null

	if (isError) {
		return (
			<div>
				<PageHeader eyebrow="Explore" title="Animal Health" />
				<p className="text-ds-text-muted">Could not load the brand catalogue. Please retry.</p>
			</div>
		)
	}

	return (
		<div>
			<PageHeader
				eyebrow="Explore"
				title="Animal Health"
				subtitle="Every brand with classified assets. Narrow by region and market, or search by name - then open a brand for its products, rules and deliverables."
			/>

			<div className="mb-4 flex justify-end">
				<AddCatalogEntry />
			</div>

			<GeoFilterBar
				regions={regionOptions(regions)}
				countries={countryOptions(countriesByRegion, regionCode)}
				regionCode={regionCode}
				countryCode={countryCode}
				query={query}
				// Switching region drops the market: a market never spans two regions.
				onRegionChange={(code) => patch({ region: code, country: null })}
				onCountryChange={(code) => patch({ region: regionCode, country: code })}
				onQueryChange={(q) => patch({ q }, true)}
				onClearAll={() => patch({ region: null, country: null, q: null })}
			/>

			<BrandResults
				brands={brands}
				totalInScope={inScope.length}
				scopeLabel={scopeLabel}
				query={query}
				// The DEEPEST geography actually chosen: a market when one is, else the region.
				// Both are values of the same axis, and the closure rolls a region's markets up,
				// so a region-only scope narrows the brand page exactly as far as it was narrowed
				// here — rather than being dropped on the way in.
				market={countryCode ?? regionCode}
				isLoading={isLoading}
				onClearSearch={() => patch({ q: null }, true)}
			/>
		</div>
	)
}
